import {
  ERROR_KEYS,
  PERMISSION_PREVIEW_END_REASONS,
  type PermissionPreviewEndReason,
} from '@repo/utils';

export const PERMISSION_PREVIEW_EVENT = 'permission-preview:signal';

export const PERMISSION_PREVIEW_EVENT_TYPES = {
  READ_ONLY: 'read-only',
  ENDED: 'ended',
  TENANT_MISMATCH: 'tenant-mismatch',
} as const;

export type PermissionPreviewEventType =
  (typeof PERMISSION_PREVIEW_EVENT_TYPES)[keyof typeof PERMISSION_PREVIEW_EVENT_TYPES];

export interface PermissionPreviewEventDetail {
  type: PermissionPreviewEventType;
  /** Solo per `ended`: assente se il backend non ne manda uno riconosciuto. */
  reason?: PermissionPreviewEndReason;
}

interface ApiErrorPayload {
  message?: string;
  params?: Record<string, unknown> | null;
  reason?: unknown;
}

const END_REASONS = Object.values(
  PERMISSION_PREVIEW_END_REASONS
) as readonly string[];

const isEndReason = (value: unknown): value is PermissionPreviewEndReason =>
  typeof value === 'string' && END_REASONS.includes(value);

/**
 * Motivo di chiusura riconosciuto, o `undefined`: un valore che il client non
 * conosce ancora (il backend può aggiungerne) ricade sul testo generico.
 */
export const toPermissionPreviewEndReason = (
  value: unknown
): PermissionPreviewEndReason | undefined =>
  isEndReason(value) ? value : undefined;

/**
 * Il motivo viaggia nei `params` dell'`I18nException`; il campo di primo
 * livello è una tolleranza verso forme diverse del payload.
 */
const resolveEndReason = (
  payload: ApiErrorPayload
): PermissionPreviewEndReason | undefined => {
  return toPermissionPreviewEndReason(payload.params?.reason ?? payload.reason);
};

/** Mappa la risposta d'errore dell'API sull'evento dell'anteprima permessi. */
export const resolvePermissionPreviewEventDetail = (
  payload: ApiErrorPayload | null | undefined
): PermissionPreviewEventDetail | null => {
  switch (payload?.message) {
    case ERROR_KEYS.PERMISSION_PREVIEW.READ_ONLY:
      return { type: PERMISSION_PREVIEW_EVENT_TYPES.READ_ONLY };
    case ERROR_KEYS.PERMISSION_PREVIEW.ENDED: {
      const reason = resolveEndReason(payload);
      return reason
        ? { type: PERMISSION_PREVIEW_EVENT_TYPES.ENDED, reason }
        : { type: PERMISSION_PREVIEW_EVENT_TYPES.ENDED };
    }
    case ERROR_KEYS.PERMISSION_PREVIEW.TENANT_MISMATCH:
      return { type: PERMISSION_PREVIEW_EVENT_TYPES.TENANT_MISMATCH };
    default:
      return null;
  }
};

export const dispatchPermissionPreviewEvent = (
  detail: PermissionPreviewEventDetail
) =>
  window.dispatchEvent(
    new CustomEvent<PermissionPreviewEventDetail>(PERMISSION_PREVIEW_EVENT, {
      detail,
    })
  );

/** Sottoscrive gli eventi dell'anteprima; restituisce la funzione di rilascio. */
export const subscribePermissionPreviewEvent = (
  listener: (detail: PermissionPreviewEventDetail) => void
): (() => void) => {
  const handler = (event: Event) =>
    listener((event as CustomEvent<PermissionPreviewEventDetail>).detail);
  window.addEventListener(PERMISSION_PREVIEW_EVENT, handler);
  return () => window.removeEventListener(PERMISSION_PREVIEW_EVENT, handler);
};
